import * as Highcharts from 'highcharts';
import { ChartValueFormat, TrajectoryChart } from 'src/app/leader-toolkit/models/hospital-dashboard.model';

const THEME = {
  primary: '#0f9d89',
  text: '#6b7280',
  border: '#e5e7eb',
  grid: '#eef1f2',
  flatHighlight: '#f59e0b',
};

function formatTooltipValue(value: number, format: ChartValueFormat): string {
  if (!Number.isFinite(value)) return '—';
  switch (format) {
    case 'currency':
      return '$' + value.toLocaleString('en-US', { maximumFractionDigits: 0 });
    case 'percent':
      return value.toLocaleString('en-US', { maximumFractionDigits: 1 }) + '%';
    default:
      return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
}

/**
 * Builds a Highcharts line chart for a goal's "Actual vs. required trajectory".
 * A single code path serves every goal — the y-axis clamps to the data and the
 * remaining-weeks region is drawn as a plot band.
 */
export function buildTrajectoryChartOptions(trajectory: TrajectoryChart): Highcharts.Options {
  const { categories, actual, required, valueFormat, yMin, yMax, flatHighlight } = trajectory;

  const remainingStart = categories.length > 1 ? categories.length - 1 : categories.length;

  return {
    chart: {
      type: 'line',
      height: 180,
      backgroundColor: 'transparent',
      spacingTop: 8,
      spacingBottom: 6,
      spacingLeft: 8,
      spacingRight: 8,
    },
    title: { text: '' },
    credits: { enabled: false },
    legend: {
      enabled: true,
      align: 'right',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      itemStyle: { fontSize: '11px', color: THEME.text },
      symbolRadius: 3,
      symbolHeight: 6,
      symbolWidth: 6,
    },
    tooltip: {
      shared: true,
      pointFormatter: function (this: Highcharts.Point) {
        const point = this as unknown as { series: { name: string }; y: number | null };
        if (point.y == null) return '';
        return `<b>${point.series.name}</b>: ${formatTooltipValue(point.y, valueFormat)}<br/>`;
      },
    },
    xAxis: {
      categories,
      labels: { style: { fontSize: '10px', color: THEME.text } },
      lineColor: THEME.border,
      tickLength: 0,
      plotBands: [
        {
          from: remainingStart,
          to: categories.length - 1,
          color: 'rgba(15, 157, 137, 0.05)',
          label: {
            text: 'remaining',
            style: { fontSize: '9px', color: THEME.text },
            y: 4,
          },
        },
      ],
    },
    yAxis: {
      title: { text: '' },
      labels: {
        style: { fontSize: '10px', color: THEME.text },
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
          return formatTooltipValue(this.value as number, valueFormat);
        },
      },
      gridLineColor: THEME.grid,
      min: yMin,
      max: yMax,
      startOnTick: false,
      endOnTick: false,
    },
    plotOptions: {
      line: {
        marker: { enabled: true, radius: 3, symbol: 'circle' },
        lineWidth: 2.5,
        states: { hover: { lineWidth: 3 } },
      },
      series: {
        animation: { duration: 400 },
      },
    },
    series: [
      {
        name: 'Actual',
        type: 'line',
        color: THEME.primary,
        data: actual,
        marker: {
          enabled: true,
          radius: 3,
          fillColor: THEME.primary,
          lineColor: THEME.primary,
        },
      },
      {
        name: 'Required pace',
        type: 'line',
        color: THEME.text,
        dashStyle: 'Dash',
        data: required,
        marker: { enabled: false },
        lineWidth: 2,
        zIndex: 1,
      },
    ],
    annotations:
      flatHighlight && actual.length > flatHighlight.end
        ? ([
            {
              labelOptions: { style: { fontSize: '9px', color: '#b7770d' } },
              labels: [
                {
                  point: {
                    xAxis: 0,
                    yAxis: 0,
                    x: flatHighlight.start,
                    y: actual[flatHighlight.end],
                  },
                  text: flatHighlight.label,
                  y: -18,
                },
              ],
              shapes: [
                {
                  type: 'rect' as const,
                  xAxis: 0,
                  yAxis: 0,
                  from: flatHighlight.start - 0.45,
                  to: flatHighlight.end + 0.45,
                  fromY: Math.min(...actual.slice(flatHighlight.start, flatHighlight.end + 1)) - 2,
                  toY: Math.max(...actual.slice(flatHighlight.start, flatHighlight.end + 1)) + 2,
                  fill: 'rgba(245, 158, 11, 0.08)',
                  stroke: 'rgba(245, 158, 11, 0.6)',
                  strokeWidth: 1,
                  dashStyle: 'ShortDash' as const,
                },
              ],
            },
          ] as unknown as Highcharts.AnnotationsOptions[])
        : undefined,
  };
}